<template>
  <el-table
    class="simpleTable"
    ref="tableRef"
    :height="height ? height + 'px' : undefined"
    :data="tableData"
    v-loading="listLoading"
    border
    fit
    highlight-current-row
    style="width:100%"
    row-key="id"
    :default-expand-all="defaultExpandAll"
    :cell-style="cellStyle"
    :row-class-name="rowClassName"
    :span-method="spanMethod"
    @selection-change="handleSelectionChange"
  >
    <el-table-column
      v-if="hadSelect"
      type="selection"
      :selectable="selectable"
      width="50"
    />
    <el-table-column
      :key="'t-column'+index"
      v-for="(item, index) in normalColumns"
      :prop="item.prop"
      :label="item.label"
      :width="item.width ? item.width : null"
      :sortable="item.sortable"
      :formatter="item.formatter"
      header-align="left"
      :align="item.align"
      show-overflow-tooltip
    />
    <el-table-column
      :key="'t-column-slot' + index"
      v-for="(item, index) in slotColumns"
      :prop="item.prop"
      :label="item.label"
      :width="item.width ? item.width : null"
      :sortable="item.sortable"
      header-align="left"
      :align="item.align"
      show-overflow-tooltip
    >
      <template #default="{row, $index}">
        <slot
          :name="`slot-${item.prop}`"
          :row="row"
          :column="item"
          :index="$index"
        >
          {{ row[item.prop] }}
        </slot>
      </template>
    </el-table-column>
    <el-table-column
      :key="'t-column-icon'+index"
      v-for="(item, index) in iconColumns"
      :prop="item.prop"
      :label="item.label"
      :width="item.width ? item.width : null"
      :sortable="item.sortable"
      :formatter="item.formatter"
      header-align="left"
      :align="item.align"
      show-overflow-tooltip
    >
      <template #default="{row}">
        <el-tooltip
          v-if="item.showIcon(row)"
          :content="row[item.iconTooltip]"
          placement="left-end"
        >
          <i
            style="font-size: 20px; color:#ff4949; padding-right: 5px;"
            :class="'el-icon-' + item.icon"
          />
        </el-tooltip>
        <span>{{ item.formatter(row) }}</span>
      </template>
    </el-table-column>
    <el-table-column
      v-if="hadOperate"
      :label="t(operateText)"
      :width="operateColumnWidth"
      show-overflow-tooltip
    >
      <template #default="{row}">
        <slot
          :row="row"
        />
      </template>
    </el-table-column>
  </el-table>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  props: {
    height: {
      type: Number,
      required: false,
      default: null
    },
    tableData: {
      type: Array,
      required: true
    },
    columnStruct: {
      type: Array,
      required: true
    },
    listLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    hadOperate: {
      type: Boolean,
      required: false,
      default: true
    },
    operateText: {
      type: String,
      required: false,
      default: 'common.operate'
    },
    operateColumnWidth: {
      type: Number,
      required: false,
      default: 180
    },
    hadSelect: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultExpandAll: {
      type: Boolean,
      required: false,
      default: true
    },
    selectable: {
      type: Function,
      required: false,
      default: () => true
    },
    cellStyle: {
      type: Function,
      required: false,
      default: () => { return {} }
    },
    rowClassName: {
      type: Function,
      required: false,
      default: null
    },
    spanMethod: {
      type: Function,
      required: false,
      default: null
    }
  },
  setup(props) {
    const { t } = useI18n()
    const tableRef = ref()
    const selectedRows = ref<any[]>([])

    const getSelectionRows = () => {
      return selectedRows.value || []
    }
    const clearSelection = () => {
      if (tableRef.value) {
        tableRef.value.clearSelection()
        selectedRows.value = []
      }
    }
    const handleSelectionChange = (val: any) => {
      selectedRows.value = val
    }
    const normalColumns = computed(() =>
      props.columnStruct.filter((c: any) => !c.icon && !c.slot)
    )

    const slotColumns = computed(() =>
      props.columnStruct.filter((c: any) => !!c.slot)
    )

    const iconColumns = computed(() =>
      props.columnStruct.filter((c: any) => !!c.icon)
    )
    onMounted(() => {
      nextTick(() => {
        if (tableRef.value && tableRef.value.doLayout) {
          tableRef.value.doLayout()
        }
      })
    })
    return {
      t,
      tableRef,
      normalColumns,
      slotColumns,
      iconColumns,
      handleSelectionChange,
      getSelectionRows,
      clearSelection
    }
  }
})
</script>
<style scoped>
::v-deep(.warning-row) {
  background-color: var(--color-pale-yellow) !important;
}

:deep(.summary-row) {
  font-weight: bold !important;
}
.simpleTable::before {
  z-index: inherit;
}
</style>
